// packages/renderer/src/db/edb/file-stamp.ts
//
// What this browser last knew about a `.edb` on disk.
//
// The problem: a file in the workspace folder has more than one writer. Two tabs
// on different origins — `localhost:5190` and `localhost:5191` are different
// origins — share the folder and nothing else: the OPFS pool holding the imported
// copy, the IndexedDB handle store and the folder index are all origin-scoped, so
// each origin holds its own copy of the same file. The same is true of another
// browser profile, or a folder synced between machines.
//
// Nothing about a copy says whether the file has moved on since it was made: the
// pool exposes no timestamps and the FileSystem Access API has no change events.
// So we record what the file looked like at the moment our copy and it agreed —
// on import, and after every write — and compare that with the file now.
//
// Device-local, and a CACHE. No stamp, or one from another version of the app,
// answers `unknown`, and every caller treats that as "leave both copies alone".

/** What can be read about a file without reading the file. */
export interface FileFacts {
  mtime: number;
  size: number;
}

export interface FileStamp extends FileFacts {
  /**
   * Our copy holds changes this file does not.
   *
   * Recorded rather than derived: the autosave policy's dirty flag lives in
   * memory and the decisions that need this run at BOOT, before any of that
   * exists (see `space-adopt.ts`).
   */
  dirty?: boolean | undefined;
  /**
   * When our copy last changed, as epoch ms. Absent while it agrees with the file.
   *
   * The answer to a question the "which copy do you want?" prompts could not
   * answer: the file side said `saved 30 Sept 2026, 12:59` and the browser side
   * said nothing at all about WHEN, so the one thing the reader was trying to work
   * out — which copy is later — was the one thing not on screen.
   *
   * It belongs here rather than in memory for the same reason `dirty` does: the
   * prompts run at boot, and an in-memory time would be `undefined` in exactly the
   * session that needs it.
   */
  changedAt?: number | undefined;
}

/** How our copy of a file stands against the file itself. */
export type FileVerdict =
  /** The file is as we left it. Nothing to do. */
  | 'same'
  /** Something else wrote the file. Ours is behind. */
  | 'file-newer'
  /** Both moved: the file was written AND we hold unsaved changes. */
  | 'conflict'
  /** We hold unsaved changes and the file has not moved. Ours is ahead. */
  | 'ahead'
  /** Never agreed, or the file is gone. Nothing can be concluded. */
  | 'unknown';

const KEY = 'eda:fileStamps';

function readAll(): Record<string, FileStamp> {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, FileStamp>) : {};
  } catch {
    return {}; // private mode, or a value from another version
  }
}

function writeAll(all: Record<string, FileStamp>): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(all));
  } catch {
    /* private mode or out of room: every verdict becomes `unknown`, which changes nothing */
  }
}

export function readStamp(file: string): FileStamp | null {
  const s = readAll()[file];
  return s && typeof s.mtime === 'number' && typeof s.size === 'number' ? s : null;
}

/**
 * Our copy and the file are the same right now — an import, or a write we just
 * made.
 *
 * Writes the whole stamp rather than patching it, which is what drops both
 * `dirty` and `changedAt`: there are no unsaved changes, so there is no time at
 * which our copy last moved away from the file.
 */
export function recordAgreement(file: string, facts: FileFacts): void {
  const all = readAll();
  all[file] = { mtime: facts.mtime, size: facts.size };
  writeAll(all);
}

/**
 * How often {@link markLocalChanges} may rewrite the stamp once it is dirty.
 *
 * It runs from the store's change broadcast — once per write, and an import is
 * thousands — so recording every one would put a `localStorage` write behind every
 * row. A second is far finer than the thing this feeds: a dialog that reads
 * `changed 30 Sept 2026, 13:40`.
 */
const CHANGE_RESOLUTION_MS = 1000;

/**
 * Our copy has moved on from the file.
 *
 * Only meaningful once there IS a stamp: a file we have never agreed with is
 * `unknown` either way, and inventing a stamp for it would claim knowledge of a
 * file nobody has read.
 *
 * The first change since the last save is what flips `dirty`; every change after
 * it moves `changedAt`, which is what the prompts show. It used to return early
 * on an already-dirty stamp, so the recorded time — had there been one — would
 * have been the OLDEST unsaved change rather than the newest, and the reader
 * comparing it with the file's save time would have drawn the wrong conclusion.
 */
export function markLocalChanges(file: string, now: number = Date.now()): void {
  const all = readAll();
  const stamp = all[file];
  if (!stamp) return;
  if (stamp.dirty === true && now - (stamp.changedAt ?? 0) < CHANGE_RESOLUTION_MS) return;
  all[file] = { ...stamp, dirty: true, changedAt: now };
  writeAll(all);
}

/**
 * We have just written our copy over the file, so we know both: what the file
 * looks like now, and that it is not a copy of this database.
 *
 * The honest stamp after an Overwrite. `clearStamp` was used here, which read as
 * modest — we can no longer describe the relationship — but it BLINDED the tab:
 * with no stamp every later verdict is `unknown`, so the one machine that had just
 * pushed its work out could never again be told that another had pushed theirs.
 * Recording the file's facts with `dirty` keeps the pair comparable, and the next
 * outside write comes back as `conflict` rather than as silence.
 */
export function recordDivergence(file: string, facts: FileFacts, now: number = Date.now()): void {
  const all = readAll();
  all[file] = { mtime: facts.mtime, size: facts.size, dirty: true, changedAt: now };
  writeAll(all);
}

/**
 * Does this browser hold work that is in no file? The question a BOOT asks.
 *
 * The header's red dot used to come from the autosave policy alone, and that flag
 * is a closure variable built fresh on every load. So a reload painted "everything
 * here is saved" over a workspace whose edits had never reached the disk — and the
 * one cue for "press Save" went out at exactly the moment it was needed.
 *
 * The stamp outlives the reload, so the stamp answers. Three cases, and the two
 * that cannot be proved clean are called dirty:
 *
 * - **No file at all** — the workspace lives in this browser's own database. None
 *   of it is on disk, which is what the dot says.
 * - **A file with no stamp** — we have never read that file on this origin. A
 *   guess either way, and "saved" is the guess that costs the user work.
 * - **A file with a stamp** — it knows, because every write records it
 *   ({@link markLocalChanges}) and every save clears it ({@link recordAgreement}).
 */
export function holdsUnsavedWork(adoptedFile: string | null): boolean {
  if (adoptedFile === null) return true;
  const stamp = readStamp(adoptedFile);
  return stamp === null || stamp.dirty === true;
}

/**
 * The browser side's date, ready to spread into a `CopyFacts`.
 *
 * Every "which copy do you want?" prompt needs it and every one of them reaches
 * it the same way, so the four of them share this rather than each remembering to
 * ask. Empty when there is nothing to date — no stamp, or a copy that still
 * agrees with its file — which is what leaves the line reading as it always did.
 *
 * Typed structurally rather than as a `CopyFacts`: this module is the RECORD of
 * what a file looked like, and `copy-facts.ts` is what renders a comparison. The
 * record should not have to import the renderer.
 */
export function changedHere(file: string): { changedAt?: number | undefined } {
  const at = readStamp(file)?.changedAt;
  return at === undefined ? {} : { changedAt: at };
}

/** Forget what we knew, so the next comparison is `unknown` and touches nothing. */
export function clearStamp(file: string): void {
  const all = readAll();
  if (!(file in all)) return;
  delete all[file];
  writeAll(all);
}

export function factsOf(file: File): FileFacts {
  return { mtime: file.lastModified, size: file.size };
}

/** The file's facts, or null when it cannot be read (gone, or permission lapsed). */
export async function factsOfHandle(handle: FileSystemFileHandle): Promise<FileFacts | null> {
  try {
    return factsOf(await handle.getFile());
  } catch {
    return null;
  }
}

/**
 * Compare a recorded stamp with the file as it is now.
 *
 * `mtime` AND `size`, because a file written twice inside the same millisecond is
 * a real case for a program writing it — and because a filesystem that rounds
 * timestamps still changes the size when the contents do.
 */
export function compareWithFile(stamp: FileStamp | null, current: FileFacts | null): FileVerdict {
  if (!stamp || !current) return 'unknown';
  const moved = current.mtime !== stamp.mtime || current.size !== stamp.size;
  if (stamp.dirty === true) return moved ? 'conflict' : 'ahead';
  return moved ? 'file-newer' : 'same';
}

/**
 * Files the user has been asked about before a write, and told to leave alone.
 *
 * Keyed by the facts they answered ABOUT, so the answer stops applying the moment
 * the file changes again. Without this an autosave timer asks the same question
 * every tick: the verdict does not improve by being declined, and a modal every
 * thirty seconds is worse than the problem it reports.
 *
 * In memory, not `localStorage`, and deliberately: this is a decision about the
 * session in front of you. A reload is a fresh look at the file, and being asked
 * once more after one is cheap; silently carrying "leave it" across a restart is
 * not.
 */
const declined = new Map<string, string>();

const factsKey = (f: FileFacts): string => `${f.mtime}:${f.size}`;

/** The user chose not to write over the file as it stands. */
export function markWriteDeclined(file: string, facts: FileFacts): void {
  declined.set(file, factsKey(facts));
}

/** Have we already asked about this file in exactly this state? */
export function writeDeclined(file: string, facts: FileFacts): boolean {
  return declined.get(file) === factsKey(facts);
}

/** Forget a decline — the file is ours again after a write or a load. */
export function clearWriteDeclined(file: string): void {
  declined.delete(file);
}

/** Convenience for the two callers that only ask about one file by name. */
export async function verdictFor(file: string, handle: FileSystemFileHandle | null): Promise<FileVerdict> {
  return compareWithFile(readStamp(file), handle ? await factsOfHandle(handle) : null);
}
